import React, { useEffect, useMemo, useState } from 'react'
import whatsappApi from '../whatsapp/whatsappApi'
import SessionManager from '../../Utils/SessionManager'
import myToast from '../../Utils/MyToast'
import MdlSelectMultipleStudent from '../../dialog/MdlSelectMultipleStudent'
import {
  formatTemplateName,
  formatVariableLabel,
  getTemplateVariables,
  buildServiceKeys,
  templateMatchesService,
} from '../whatsapp/waTemplateUtils'
import CommTemplatePickerDialog from './CommTemplatePickerDialog'
import CommSendConfirmDialog from './CommSendConfirmDialog'

const WA = '#25D366'
const WA_DARK = '#128C7E'

// Created By Cursor
function getCurrentYearMonth() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

// Created By Cursor
function buildSessionMonths() {
  try {
    const mSession = SessionManager.getCurrentSession()
    const start = new Date(mSession[0].start_date)
    const end = new Date(mSession[0].end_date)
    const months = []
    while (start <= end) {
      months.push(
        `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}`
      )
      start.setMonth(start.getMonth() + 1)
    }
    return months.length ? months : [getCurrentYearMonth()]
  } catch (_) {
    return [getCurrentYearMonth()]
  }
}

// Created By Cursor
function formatMonthLabel(value) {
  const m = String(value || '').match(/^(\d{4})-(\d{2})$/)
  if (!m) return String(value || '')
  const names = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ]
  const idx = parseInt(m[2], 10) - 1
  return names[idx] ? `${names[idx]} ${m[1]}` : String(value)
}

// Created By Cursor — last calendar day of YYYY-MM
function lastDayLabel(ym) {
  const m = String(ym || '').match(/^(\d{4})-(\d{2})$/)
  if (!m) return formatMonthLabel(ym)
  const y = Number(m[1])
  const mo = Number(m[2])
  const d = new Date(y, mo, 0)
  return d.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

// Created By Cursor — map template variable labels → values for a due row
function buildFeeValueMap(row, ctx) {
  const amount = Number(row?.due_amount || 0).toFixed(2)
  const monthLabel = formatMonthLabel(ctx.month)
  const className = String(
    row?.class_name || row?.class_id || ''
  ).trim()
  const sectionName = String(
    row?.section_name || row?.section_id || ''
  ).trim()
  const classDisplay =
    className && sectionName && sectionName !== 'No'
      ? `${className}-${sectionName}`
      : className || sectionName || '-'

  const parentName = String(row?.parent_name || 'Parent').trim() || 'Parent'
  const studentName = String(row?.student_name || '').trim()

  return {
    parent_name: parentName,
    fee_name: 'School Fee',
    amount,
    student_name: studentName,
    due_date: lastDayLabel(ctx.month),
    school_name: String(ctx.schoolName || 'School').trim() || 'School',
    class_name: classDisplay,
    class_section: classDisplay,
    class: classDisplay,
    class_id: String(row?.class_id || '').trim(),
    section_name: sectionName || '-',
    section: sectionName || '-',
    section_id: String(row?.section_id || '').trim(),
    month: monthLabel,
    fee: 'School Fee',
    fees: 'School Fee',
    due_amount: amount,
    fee_amount: amount,
    name: studentName,
    student: studentName,
    parent: parentName,
  }
}

// Created By Cursor — resolve {{n}} by label / aliases (no wrong positional overwrite)
function resolveTemplateValue(v, valueMap, variables) {
  const rawLabel = String(v.label || '')
    .toLowerCase()
    .trim()
  const key = rawLabel.replace(/\s+/g, '_')

  const aliases = {
    class: 'class_name',
    classname: 'class_name',
    class_name: 'class_name',
    class_section: 'class_section',
    classsection: 'class_section',
    std_class: 'class_name',
    student_class: 'class_name',
    section: 'section_name',
    section_name: 'section_name',
    amount: 'amount',
    fee_amount: 'amount',
    due_amount: 'amount',
    fee: 'fee_name',
    fees: 'fee_name',
    fee_name: 'fee_name',
    student: 'student_name',
    student_name: 'student_name',
    parent: 'parent_name',
    parent_name: 'parent_name',
    father_name: 'parent_name',
    due: 'due_date',
    due_date: 'due_date',
    school: 'school_name',
    school_name: 'school_name',
  }

  const read = (k) => {
    if (!k) return null
    const val = valueMap[k]
    if (val == null) return null
    const s = String(val).trim()
    return s !== '' ? s : null
  }

  // 1) Direct / alias from this variable's label
  if (key) {
    const hit =
      read(key) ||
      read(aliases[key]) ||
      (key === 'class_section' || key === 'classsection'
        ? read('class_name')
        : null)
    if (hit) return hit
  }

  // 2) Label from variables list for this index
  const byIndex = (variables || []).find((x) => Number(x.index) === Number(v.index))
  const idxLabel = String(byIndex?.label || '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_')
  if (idxLabel) {
    const hit =
      read(idxLabel) ||
      read(aliases[idxLabel]) ||
      (idxLabel === 'class_section' || idxLabel === 'classsection'
        ? read('class_name')
        : null)
    if (hit) return hit
  }

  // 3) If label clearly means class / class_section
  const labelHint = key || idxLabel
  if (labelHint && /class/.test(labelHint)) {
    return (
      read('class_section') ||
      read('class_name') ||
      read('class') ||
      read('class_id') ||
      '-'
    )
  }

  // 4) Positional defaults for Meta Fee Due Reminder:
  // Dear {{1}} parent · {{2}} student · Class {{3}} · Rs. {{4}} amount
  if (!key && !idxLabel) {
    const defaults = [
      'parent_name',
      'student_name',
      'class_section',
      'amount',
      'due_date',
    ]
    const defKey = defaults[Number(v.index) - 1]
    const hit = read(defKey) || (defKey === 'class_section' ? read('class_name') : null)
    if (hit) return hit
  }

  return ''
}

// Created By Cursor — infer slot meaning from body text when labels are stale
function inferLabelFromBody(body, index) {
  const n = Number(index)
  const b = String(body || '')
  if (!n || !b) return ''
  if (new RegExp(`Dear\\s+\\{\\{${n}\\}\\}`, 'i').test(b)) return 'parent_name'
  if (
    new RegExp(
      `(?:fee due for|due for|for)\\s+\\{\\{${n}\\}\\}\\s*\\(\\s*Class`,
      'i'
    ).test(b)
  ) {
    return 'student_name'
  }
  if (new RegExp(`(?:\\(\\s*)?Class\\s*\\{\\{${n}\\}\\}`, 'i').test(b)) {
    return 'class_section'
  }
  if (new RegExp(`(?:Rs\\.?|INR|₹)\\s*\\{\\{${n}\\}\\}`, 'i').test(b)) {
    return 'amount'
  }
  if (new RegExp(`due on\\s+\\{\\{${n}\\}\\}`, 'i').test(b)) return 'due_date'
  if (new RegExp(`the\\s+\\{\\{${n}\\}\\}\\s+fee`, 'i').test(b)) return 'fee_name'
  return ''
}

// Created By Cursor
function fillTemplatePreview(body, variables, valueMap) {
  let out = String(body || '')
  const indices = [
    ...new Set(
      [...String(body).matchAll(/\{\{(\d+)\}\}/g)].map((m) => Number(m[1]))
    ),
  ].sort((a, b) => a - b)

  indices.forEach((n) => {
    const fromVars =
      (variables || []).find((x) => Number(x.index) === n) || null
    const inferred = inferLabelFromBody(body, n)
    const v = {
      index: n,
      label: inferred || fromVars?.label || '',
    }
    const val = resolveTemplateValue(v, valueMap, variables)
    out = out.replace(
      new RegExp(`\\{\\{${n}\\}\\}`, 'g'),
      val !== '' ? val : `{{${n}}}`
    )
  })
  return out
}

// Created By Cursor — Rec 4: build params from template body {{n}} (backend DB pad/trim is backup)
function buildBodyParams(variables, valueMap, template) {
  const body =
    template?.body_text || template?.body || template?.message || ''
  const indices = [
    ...new Set(
      [...String(body).matchAll(/\{\{(\d+)\}\}/g)].map((m) => Number(m[1]))
    ),
  ].sort((a, b) => a - b)

  const slots =
    indices.length > 0
      ? indices
      : (variables || []).map((v) => Number(v.index)).filter(Boolean)

  if (!slots.length) {
    // Fallback for body with no placeholders: parent, student, class, amount
    return [
      valueMap.parent_name,
      valueMap.student_name,
      valueMap.class_section || valueMap.class_name,
      valueMap.amount,
    ]
  }

  return slots.map((n) => {
    const fromVars =
      (variables || []).find((x) => Number(x.index) === n) || null
    const inferred = inferLabelFromBody(body, n)
    const v = {
      index: n,
      label: inferred || fromVars?.label || '',
    }
    const val = resolveTemplateValue(v, valueMap, variables)
    if (val) return val
    if (/class/.test(String(v.label || ''))) {
      return valueMap.class_section || valueMap.class_name || '-'
    }
    return ''
  })
}

// Created By Cursor — merge class/section from student picker into due rows
function enrichRecipientsWithSelection(rows, students) {
  const byId = {}
  ;(students || []).forEach((s) => {
    const id = String(s.student_enrollment_no || s.student_id || '')
    if (id) byId[id] = s
  })
  return (rows || []).map((r) => {
    const s = byId[String(r.student_id)] || {}
    return {
      ...r,
      class_id: r.class_id || s.class_id || '',
      section_id: r.section_id || s.section_id || '',
      class_name: r.class_name || s.class_name || r.class_id || s.class_id || '',
      section_name:
        r.section_name || s.section_name || r.section_id || s.section_id || '',
      student_name: String(r.student_name || s.stu_name || s.student_name || '').trim(),
      parent_name: String(r.parent_name || s.father_name || 'Parent').trim(),
    }
  })
}

// Created By Cursor
const CommComposeFeeReminder = ({
  service,
  connected = false,
  onStatusChange,
}) => {
  const [sessionId, setSessionId] = useState('')
  const [templates, setTemplates] = useState([])
  const [templateId, setTemplateId] = useState('')
  const [selectedStudents, setSelectedStudents] = useState([])
  const [selectedMonth, setSelectedMonth] = useState(getCurrentYearMonth())
  const [recipients, setRecipients] = useState([])
  const [selected, setSelected] = useState({})
  const [schoolName, setSchoolName] = useState('')
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [isOpenSelectStudent, setIsOpenSelectStudent] = useState(false)
  const [showTemplateDialog, setShowTemplateDialog] = useState(false)
  const [templateSearch, setTemplateSearch] = useState('')
  const [confirmSend, setConfirmSend] = useState(null)

  const monthOptions = useMemo(() => buildSessionMonths(), [])
  const serviceKeys = useMemo(() => buildServiceKeys(service), [service])

  const visibleTemplates = useMemo(() => {
    if (!serviceKeys.size) return templates
    const filtered = templates.filter((t) => templateMatchesService(t, serviceKeys))
    return filtered.length ? filtered : templates
  }, [templates, serviceKeys])

  useEffect(() => {
    try {
      const s = SessionManager.getCurrentSession()
      if (s && s[0]) setSessionId(s[0].session_id || s[0].id || '')
    } catch (e) {
      /* ignore */
    }
    whatsappApi.getTemplates().then((res) => {
      if (res.api_status === 'OK') {
        const list = (res.data.templates || []).filter((t) => t.status === 'approved')
        setTemplates(list.length ? list : res.data.templates || [])
      }
    })
  }, [])

  useEffect(() => {
    if (!templateId) return
    const stillValid = visibleTemplates.some((t) => String(t.id) === String(templateId))
    if (!stillValid) setTemplateId('')
  }, [visibleTemplates, templateId])

  const selectedTemplate = useMemo(
    () =>
      visibleTemplates.find((t) => String(t.id) === String(templateId)) ||
      templates.find((t) => String(t.id) === String(templateId)) ||
      null,
    [visibleTemplates, templates, templateId]
  )

  const selectedVariables = useMemo(
    () => getTemplateVariables(selectedTemplate),
    [selectedTemplate]
  )

  const selectedBodyText =
    selectedTemplate?.body_text ||
    selectedTemplate?.body ||
    selectedTemplate?.message ||
    ''
  const selectedCategory = selectedTemplate?.category || ''

  const dialogTemplates = useMemo(() => {
    const q = templateSearch.trim().toLowerCase()
    if (!q) return visibleTemplates
    return visibleTemplates.filter((t) =>
      [t.name, t.status, t.category, t.group_name, t.service_code, t.body_text]
        .map((v) => String(v || '').toLowerCase())
        .join(' ')
        .includes(q)
    )
  }, [visibleTemplates, templateSearch])

  // Created By Cursor
  const openTemplateDialog = () => {
    setTemplateSearch('')
    setShowTemplateDialog(true)
  }

  // Created By Cursor
  const pickTemplate = (tpl) => {
    if (!tpl?.id) return
    setTemplateId(String(tpl.id))
    setShowTemplateDialog(false)
  }

  // Created By Cursor
  const loadDues = async (
    students = selectedStudents,
    month = selectedMonth,
    sid = sessionId
  ) => {
    const useSession =
      sid || students?.[0]?.session_id || sessionId
    if (!useSession) {
      myToast.notify_error('Session not found')
      return
    }
    if (!students?.length) {
      myToast.notify_error('Select students first')
      return
    }
    setLoading(true)
    try {
      const studentIds = students
        .map((s) => s.student_enrollment_no)
        .filter(Boolean)
      const res = await whatsappApi.getFeeDueRecipients({
        session_id: useSession,
        month,
        // comma-separated avoids JSON/bracket issues on GET query strings
        student_ids: studentIds.join(','),
        limit: Math.max(200, studentIds.length),
      })
      if (res.api_status === 'OK') {
        const rows = enrichRecipientsWithSelection(
          res.data.recipients || [],
          students
        )
        setRecipients(rows)
        setSchoolName(res.data.school_name || '')
        const map = {}
        rows.forEach((r) => {
          if (r.contact_number && Number(r.due_amount || 0) > 0) {
            map[r.student_id] = true
          }
        })
        setSelected(map)
        if (!rows.length) {
          myToast.notify_warning('No students found for selection')
        }
      } else {
        myToast.notify_error(res.api_remark || 'Failed to load dues')
      }
    } catch (e) {
      const apiMsg =
        e?.response?.data?.api_remark ||
        e?.response?.data?.message ||
        e?.message
      myToast.notify_error(apiMsg || 'Failed to load fee dues')
    } finally {
      setLoading(false)
    }
  }

  // Created By Cursor
  const handleSelectedStudent = (list) => {
    const students = list || []
    setIsOpenSelectStudent(false)
    setSelectedStudents(students)
    if (students.length) {
      const sid = students[0]?.session_id || sessionId
      if (sid) setSessionId(sid)
      loadDues(students, selectedMonth, sid)
    } else {
      setRecipients([])
      setSelected({})
    }
  }

  // Created By Cursor
  const changeMonth = (e) => {
    const month = e.target.value
    setSelectedMonth(month)
    if (selectedStudents.length) {
      loadDues(selectedStudents, month)
    }
  }

  const selectedList = useMemo(
    () => recipients.filter((r) => selected[r.student_id] && r.contact_number),
    [recipients, selected]
  )

  // Created By Cursor
  const studentPreview = (row) => {
    if (!selectedBodyText || !row) return ''
    return fillTemplatePreview(
      selectedBodyText,
      selectedVariables,
      buildFeeValueMap(row, { schoolName, month: selectedMonth })
    )
  }

  // Created By Cursor
  const toggle = (id) => setSelected((prev) => ({ ...prev, [id]: !prev[id] }))

  // Created By Cursor
  const toggleAll = () => {
    const withPhone = recipients.filter((r) => r.contact_number)
    const allOn =
      withPhone.length > 0 && withPhone.every((r) => selected[r.student_id])
    setSelected((prev) => {
      const next = { ...prev }
      withPhone.forEach((r) => {
        next[r.student_id] = !allOn
      })
      return next
    })
  }

  // Created By Cursor — open confirm dialog (no native alert / log dump)
  const sendToQueue = () => {
    if (!connected) {
      myToast.notify_error('Connect WhatsApp first')
      return
    }
    if (!templateId || !selectedTemplate) {
      myToast.notify_error('Select a template')
      return
    }
    if (!selectedList.length) {
      myToast.notify_error('Select at least one student with a phone number')
      return
    }

    const ctx = { schoolName, month: selectedMonth }
    const payload = selectedList.map((r) => {
      const valueMap = buildFeeValueMap(r, ctx)
      const params = buildBodyParams(
        selectedVariables,
        valueMap,
        selectedTemplate
      )
      const preview = fillTemplatePreview(
        selectedBodyText,
        selectedVariables,
        valueMap
      )
      return {
        contact_number: r.contact_number,
        student_id: r.student_id,
        recipient_type: 'STUDENT',
        message: JSON.stringify({
          preview,
          body_params: params,
        }),
      }
    })

    let firstPreview = ''
    try {
      firstPreview = JSON.parse(payload[0].message)?.preview || ''
    } catch (e) {
      firstPreview = ''
    }

    setConfirmSend({
      payload,
      preview: firstPreview,
      count: selectedList.length,
      templateName: selectedTemplate.name || '',
    })
  }

  // Created By Cursor
  const confirmSendToQueue = async () => {
    if (!confirmSend?.payload?.length || !selectedTemplate) return
    const staffId = SessionManager.getStaffDetails()?.staff_id
    setSending(true)
    try {
      const res = await whatsappApi.addToQueue({
        recipients: confirmSend.payload,
        template_id: Number(templateId),
        template_name: selectedTemplate.name || '',
        message_type: 'template',
        authorized_staff_id: staffId,
        process_now: true,
        service_code: service?.service_code || undefined,
      })
      if (res.api_status === 'OK') {
        const sent = Number(res.data?.processed?.sent || 0)
        const failed = Number(res.data?.processed?.failed || 0)
        const queued = Number(res.data?.queued?.success_count || 0)
        setConfirmSend(null)
        if (failed > 0 && sent === 0) {
          myToast.notify_error(
            queued
              ? `${failed} message${failed === 1 ? '' : 's'} failed to send`
              : 'Send failed'
          )
        } else if (failed > 0) {
          myToast.notify_warning(
            `Sent ${sent}, ${failed} failed`
          )
        } else {
          myToast.notify_success(
            sent > 0
              ? `Sent to ${sent} recipient${sent === 1 ? '' : 's'}`
              : `Queued ${queued} message${queued === 1 ? '' : 's'}`
          )
        }
        if (onStatusChange) onStatusChange()
      } else {
        myToast.notify_error(res.api_remark || 'Send failed')
      }
    } catch (e) {
      myToast.notify_error('Send failed')
    } finally {
      setSending(false)
    }
  }

  const fieldLabel = {
    display: 'block',
    fontSize: 11,
    fontWeight: 700,
    color: '#64748b',
    letterSpacing: '0.03em',
    textTransform: 'uppercase',
    marginBottom: 4,
  }

  return (
    <div
      style={{
        border: '1px solid #e2e8f0',
        borderRadius: 12,
        background: '#fff',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          padding: '10px 12px',
          background: 'linear-gradient(180deg, #f8fafc, #f1f5f9)',
          borderBottom: '1px solid #e2e8f0',
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 13, color: '#0f172a' }}>
          Compose · Fee Reminder
        </div>
        <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>
          {selectedList.length} selected
          {recipients.length ? ` / ${recipients.length}` : ''}
          {selectedStudents.length
            ? ` · ${selectedStudents.length} picked`
            : ''}
        </div>
      </div>

      <div style={{ padding: 12 }}>
        {/* Students + Month — Demand Slip style */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 12,
            marginBottom: 14,
          }}
        >
          <div>
            <label style={fieldLabel}>Students</label>
            <button
              type="button"
              onClick={() => setIsOpenSelectStudent(true)}
              style={{
                width: '100%',
                border: 'none',
                borderRadius: 10,
                padding: '10px 14px',
                background: `linear-gradient(135deg, ${WA} 0%, ${WA_DARK} 100%)`,
                color: '#fff',
                fontSize: 13,
                fontWeight: 800,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              <i className="fa fa-hand-pointer-o" aria-hidden />
              {selectedStudents.length
                ? `${selectedStudents.length} Student(s) Selected`
                : 'Select Students Manually'}
            </button>
          </div>
          <div>
            <label style={fieldLabel}>Month</label>
            <select
              value={selectedMonth}
              onChange={changeMonth}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 10,
                border: '1.5px solid #bbf7d0',
                background: '#fff',
                color: '#0f172a',
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
                outline: 'none',
              }}
            >
              {monthOptions.map((item) => (
                <option key={item} value={item}>
                  {formatMonthLabel(item)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Template picker — same card + dialog as CommWhatsAppCompose */}
        <div style={{ marginBottom: 14 }}>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              marginBottom: 8,
            }}
          >
            <label style={{ ...fieldLabel, marginBottom: 0 }}>Template</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" onClick={openTemplateDialog} style={btnSecondary}>
                <i className="fa fa-file-text-o" style={{ marginRight: 6 }} />
                {selectedTemplate ? 'Change Template' : 'Use Template'}
              </button>
              <button
                type="button"
                onClick={() => loadDues()}
                disabled={loading || !selectedStudents.length}
                style={btnSecondary}
              >
                {loading ? 'Loading…' : 'Load Dues'}
              </button>
              <button
                type="button"
                onClick={sendToQueue}
                disabled={sending || !selectedList.length || !templateId}
                style={btnPrimary}
              >
                {sending ? 'Sending…' : `Send (${selectedList.length})`}
              </button>
            </div>
          </div>

          {selectedTemplate ? (
            <div
              style={{
                border: `1px solid ${WA}44`,
                borderRadius: 12,
                background: 'linear-gradient(180deg, #f0fdf4 0%, #fff 48%)',
                padding: '12px 14px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: selectedBodyText ? 10 : 0,
                }}
              >
                <div style={{ flex: '1 1 180px', minWidth: 140 }}>
                  <div
                    style={{
                      fontWeight: 800,
                      fontSize: 15,
                      color: '#0f172a',
                      lineHeight: 1.3,
                    }}
                  >
                    {formatTemplateName(selectedTemplate.name)}
                  </div>
                </div>
                {selectedCategory ? (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      fontSize: 11,
                      fontWeight: 800,
                      letterSpacing: '0.03em',
                      textTransform: 'uppercase',
                      color: '#0f766e',
                      background: '#ccfbf1',
                      borderRadius: 999,
                      padding: '5px 10px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {selectedCategory}
                  </span>
                ) : null}
                {selectedTemplate.status ? (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: 'capitalize',
                      color:
                        selectedTemplate.status === 'approved'
                          ? WA_DARK
                          : selectedTemplate.status === 'pending'
                          ? '#b45309'
                          : '#64748b',
                      background:
                        selectedTemplate.status === 'approved'
                          ? `${WA}18`
                          : selectedTemplate.status === 'pending'
                          ? '#fef3c7'
                          : '#f1f5f9',
                      borderRadius: 999,
                      padding: '5px 10px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {selectedTemplate.status}
                  </span>
                ) : null}
              </div>

              {selectedVariables.length > 0 && (
                <div style={{ marginBottom: selectedBodyText ? 10 : 0 }}>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 800,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      color: '#94a3b8',
                      marginBottom: 6,
                    }}
                  >
                    Used variables
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {selectedVariables.map((v) => (
                      <span
                        key={`${v.placeholder}-${v.label || v.index}`}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          fontSize: 12,
                          fontWeight: 600,
                          color: '#334155',
                          background: '#fff',
                          border: '1px solid #e2e8f0',
                          borderRadius: 8,
                          padding: '5px 10px',
                        }}
                      >
                        <code
                          style={{
                            fontSize: 11,
                            fontWeight: 800,
                            color: WA_DARK,
                            background: `${WA}14`,
                            borderRadius: 4,
                            padding: '1px 5px',
                          }}
                        >
                          {v.placeholder}
                        </code>
                        {v.label ? (
                          <span style={{ color: '#64748b' }}>
                            {formatVariableLabel(v.label)}
                          </span>
                        ) : (
                          <span style={{ color: '#cbd5e1', fontStyle: 'italic' }}>
                            Variable {v.index}
                          </span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {selectedBodyText ? (
                <div
                  style={{
                    fontSize: 13,
                    color: '#334155',
                    lineHeight: 1.55,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    background: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: 10,
                    padding: '10px 12px',
                  }}
                >
                  {selectedBodyText}
                </div>
              ) : (
                <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>
                  No body text on this template
                </div>
              )}
            </div>
          ) : (
            <div
              style={{
                border: '1px dashed #cbd5e1',
                borderRadius: 12,
                background: '#f8fafc',
                padding: '16px 14px',
                color: '#94a3b8',
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              No template selected — click Use Template to choose one
            </div>
          )}
        </div>

        {/* One row per student: half due details | half message preview */}
        <div style={{ marginBottom: 8 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              marginBottom: 10,
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 800, color: '#0f172a' }}>
              Students · Due up to {formatMonthLabel(selectedMonth)}
            </span>
            {recipients.length > 0 && (
              <button type="button" onClick={toggleAll} style={btnTiny}>
                Toggle all
              </button>
            )}
          </div>

          {loading ? (
            <div style={emptyPad}>Loading dues…</div>
          ) : !selectedStudents.length ? (
            <div style={emptyPad}>Select students and month to load dues</div>
          ) : recipients.length === 0 ? (
            <div style={emptyPad}>No due records for this selection</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {recipients.map((r) => {
                const due = Number(r.due_amount || 0)
                const preview = studentPreview(r)
                const checked = !!selected[r.student_id]
                const canSelect = !!r.contact_number
                const selectRow = () => {
                  if (!canSelect) return
                  toggle(r.student_id)
                }
                return (
                  <div
                    key={r.student_id}
                    role="button"
                    tabIndex={canSelect ? 0 : -1}
                    onClick={selectRow}
                    onKeyDown={(e) => {
                      if (!canSelect) return
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        toggle(r.student_id)
                      }
                    }}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 0,
                      border: checked
                        ? `2px solid ${WA}`
                        : '1.5px solid #e2e8f0',
                      borderRadius: 14,
                      overflow: 'hidden',
                      background: checked ? `${WA}0d` : '#fff',
                      boxShadow: checked
                        ? `0 8px 22px ${WA}28`
                        : '0 1px 3px rgba(15,23,42,0.04)',
                      cursor: canSelect ? 'pointer' : 'not-allowed',
                      opacity: canSelect ? 1 : 0.55,
                      outline: 'none',
                      transition:
                        'border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease',
                    }}
                  >
                    {/* Left half — due details (selectable segment) */}
                    <div
                      style={{
                        padding: '14px 16px',
                        borderRight: checked
                          ? `1px solid ${WA}44`
                          : '1px solid #e2e8f0',
                        background: checked
                          ? `linear-gradient(180deg, ${WA}18, ${WA}08)`
                          : '#f8fafc',
                        display: 'flex',
                        gap: 12,
                        alignItems: 'flex-start',
                      }}
                    >
                      <span
                        aria-hidden
                        style={{
                          width: 22,
                          height: 22,
                          borderRadius: 7,
                          flexShrink: 0,
                          marginTop: 1,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: checked
                            ? `2px solid ${WA_DARK}`
                            : '2px solid #cbd5e1',
                          background: checked
                            ? `linear-gradient(135deg, ${WA}, ${WA_DARK})`
                            : '#fff',
                          color: '#fff',
                          boxShadow: checked
                            ? `0 2px 8px ${WA}55`
                            : 'none',
                          transition: 'all 0.15s ease',
                        }}
                      >
                        {checked ? (
                          <i
                            className="fa fa-check"
                            style={{ fontSize: 11, fontWeight: 700 }}
                          />
                        ) : null}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontWeight: 800,
                            fontSize: 14,
                            color: '#0f172a',
                            lineHeight: 1.3,
                          }}
                        >
                          {r.student_name}
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: '#94a3b8',
                            marginTop: 2,
                            fontWeight: 600,
                          }}
                        >
                          {r.student_id}
                          {r.class_name
                            ? ` · ${r.class_name}${
                                r.section_name ? `-${r.section_name}` : ''
                              }`
                            : r.class_id
                            ? ` · ${r.class_id}${
                                r.section_id ? `-${r.section_id}` : ''
                              }`
                            : ''}
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            color: r.contact_number ? '#475569' : '#f59e0b',
                            marginTop: 6,
                            fontWeight: 600,
                          }}
                        >
                          <i className="fa fa-phone" style={{ marginRight: 5 }} />
                          {r.contact_number || 'No phone'}
                        </div>
                        <div
                          style={{
                            marginTop: 10,
                            display: 'inline-flex',
                            alignItems: 'baseline',
                            gap: 6,
                            background: due > 0 ? '#fff7ed' : '#f1f5f9',
                            borderRadius: 8,
                            padding: '6px 10px',
                          }}
                        >
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 800,
                              letterSpacing: '0.04em',
                              textTransform: 'uppercase',
                              color: '#94a3b8',
                            }}
                          >
                            Due
                          </span>
                          <span
                            style={{
                              fontSize: 18,
                              fontWeight: 800,
                              color: due > 0 ? '#c2410c' : '#64748b',
                            }}
                          >
                            ₹{due.toFixed(2)}
                          </span>
                        </div>
                        {!canSelect && (
                          <div
                            style={{
                              marginTop: 8,
                              fontSize: 11,
                              fontWeight: 700,
                              color: '#b45309',
                            }}
                          >
                            Cannot select — no phone on file
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right half — message preview */}
                    <div
                      style={{
                        padding: '14px 16px',
                        background: checked
                          ? 'linear-gradient(180deg, #ecfdf5 0%, #fff 55%)'
                          : 'linear-gradient(180deg, #f0fdf4 0%, #fff 50%)',
                        pointerEvents: 'none',
                      }}
                    >
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 800,
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                          color: WA_DARK,
                          marginBottom: 8,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 8,
                        }}
                      >
                        <span>Message Preview</span>
                        {checked && (
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 800,
                              color: WA_DARK,
                              background: `${WA}22`,
                              borderRadius: 999,
                              padding: '2px 8px',
                            }}
                          >
                            Selected
                          </span>
                        )}
                      </div>
                      {!selectedTemplate ? (
                        <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>
                          Select a template to preview
                        </div>
                      ) : (
                        <div
                          style={{
                            fontSize: 13,
                            color: '#0f172a',
                            lineHeight: 1.55,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            background: '#fff',
                            border: checked
                              ? `1px solid ${WA}55`
                              : '1px solid #d1fae5',
                            borderRadius: 10,
                            padding: '10px 12px',
                          }}
                        >
                          {preview || selectedBodyText}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {showTemplateDialog && (
        <CommTemplatePickerDialog
          serviceName="Fee Reminder"
          templates={dialogTemplates}
          selectedId={templateId}
          search={templateSearch}
          onSearch={setTemplateSearch}
          onPick={pickTemplate}
          onClose={() => setShowTemplateDialog(false)}
        />
      )}

      {confirmSend ? (
        <CommSendConfirmDialog
          title="Send Fee Reminder?"
          subtitle="Messages will be queued and sent on WhatsApp"
          recipientCount={confirmSend.count}
          templateName={confirmSend.templateName}
          preview={confirmSend.preview}
          confirmLabel="Send now"
          busy={sending}
          onConfirm={confirmSendToQueue}
          onClose={() => {
            if (!sending) setConfirmSend(null)
          }}
        />
      ) : null}

      {isOpenSelectStudent && (
        <MdlSelectMultipleStudent
          actionClose={() => setIsOpenSelectStudent(false)}
          actionHandleSelected={handleSelectedStudent}
        />
      )}
    </div>
  )
}

const btnPrimary = {
  border: 'none',
  borderRadius: 8,
  padding: '7px 12px',
  background: WA,
  color: '#fff',
  fontSize: 12,
  fontWeight: 800,
  cursor: 'pointer',
}

const btnSecondary = {
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  padding: '7px 12px',
  background: '#fff',
  color: '#334155',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
}

const btnTiny = {
  border: '1px solid #e2e8f0',
  borderRadius: 6,
  padding: '3px 8px',
  background: '#fff',
  color: '#64748b',
  fontSize: 11,
  fontWeight: 700,
  cursor: 'pointer',
}

const emptyPad = {
  padding: 24,
  textAlign: 'center',
  color: '#94a3b8',
  fontSize: 13,
  fontWeight: 600,
}

export default CommComposeFeeReminder
